import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { PageShell, SiteFooter } from '../components/Brand';
import { SiteNav } from '../components/SiteNav';

function LegalDocLayout({
  eyebrow,
  title,
  updated,
  children,
}: {
  eyebrow: string;
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <PageShell>
      <SiteNav />
      <main className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-cyan-300">
          {eyebrow}
        </p>
        <h1 className="heading-institutional mt-3 text-3xl md:text-4xl">{title}</h1>
        <p className="mt-2 font-mono text-xs text-slate-500">Last updated · {updated}</p>
        <div className="prose-legal mt-10 space-y-8 font-sans text-sm leading-relaxed text-slate-300 md:text-[15px]">
          {children}
        </div>
        <p className="mt-12 font-sans text-sm text-slate-500">
          Questions?{' '}
          <a
            href="mailto:contact@junoware.com"
            className="cursor-pointer text-cyan-300 hover:text-cyan-200"
          >
            contact@junoware.com
          </a>
          {' · '}
          <Link to="/" className="cursor-pointer text-slate-400 hover:text-cyan-300">
            Back to home
          </Link>
        </p>
      </main>
      <SiteFooter />
    </PageShell>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="heading-institutional text-lg text-slate-100">{title}</h2>
      {children}
    </section>
  );
}

export function TermsOfUse() {
  return (
    <LegalDocLayout eyebrow="Legal" title="Terms of Use" updated="October 28, 2026">
      <Section title="1. Agreement">
        <p>
          These Terms of Use govern access to and use of TractTrace websites, qualification forms,
          and related services (the &quot;Service&quot;) operated by Junoware, LLC
          (&quot;Junoware,&quot; &quot;we,&quot; &quot;us&quot;). By accessing or using the
          Service, you agree to these Terms on behalf of yourself or the organization you
          represent. The Service is offered solely as a B2B product for commercial construction
          sales and operations teams.
        </p>
      </Section>

      <Section title="2. What TractTrace is">
        <p>
          TractTrace is a{' '}
          <strong className="font-semibold text-slate-100">
            B2B commercial permit coverage product
          </strong>{' '}
          for equipment rental fleets, building material suppliers, trade subcontractors,
          commercial insurance brokers, and related sales desks. You request coverage for a
          metropolitan area. A specialist contacts you to discuss that metro. Automated daily
          portal scraping, general-contractor skip-tracing, and live HubSpot, Salesforce, or Zapier
          delivery are not part of the current public offering.
        </p>
        <p>
          TractTrace is not a consumer marketplace, MLS substitute, B2C residential investor
          product, or driving-for-dollars prospecting tool.
        </p>
      </Section>

      <Section title="3. Non-exclusive coverage, if sold">
        <p>
          Submitting the form does not create a subscription. If you later accept a written order
          for metro coverage, that coverage is{' '}
          <strong className="font-semibold text-slate-100">non-exclusive</strong> unless the order
          expressly grants exclusive territory, category, or customer rights. List prices on the
          site are Metro at $499 per month for one metropolitan area and Region at $1,996 per month
          for up to four metropolitan areas, billed monthly on a 12-month term. Those names describe
          billing scope only. They do not convey exclusivity, sole-supplier status, or a live data
          feed at checkout. There is no self-serve purchase on this site.
        </p>
      </Section>

      <Section title="4. Fair use">
        <p>
          You may not: (a) scrape, overload, or probe the site or forms; (b) submit false, stolen,
          or automated intake; (c) use the Service for unlawful targeting, harassment, or fraud; or
          (d) hold out TractTrace materials as a consumer report, legal opinion, or credit decision.
          We may refuse, throttle, or terminate access for abuse or material breach. Public visitors
          are not issued API keys, webhook secrets, or customer accounts.
        </p>
      </Section>

      <Section title="5. Public records and no award warranty">
        <p>
          Example filings on the site, and any permit facts we discuss on a call, come from public
          municipal records. They may be incomplete, delayed, misclassified, or wrong. The Service
          is provided{' '}
          <strong className="font-semibold text-slate-100">&quot;as is&quot;</strong> and{' '}
          <strong className="font-semibold text-slate-100">&quot;as available&quot;</strong> without
          warranty that a conversation, filing, or later coverage will produce a rental, material,
          or subcontract award.
        </p>
        <p>
          Submitting the audit form is a request for Junoware to call you. You remain responsible
          for your own outreach compliance (including TCPA, CAN-SPAM, and state privacy laws) if you
          later use any information we share.
        </p>
      </Section>

      <Section title="6. Fees">
        <p>
          This site does not charge a card. If you accept a written order for Metro or Region, fees
          follow that order (currently the list prices in Section 3 unless the order says
          otherwise). Fees are non-refundable except as that order states or law requires. Taxes are
          your responsibility where applicable.
        </p>
      </Section>

      <Section title="7. Intellectual property">
        <p>
          Junoware retains all rights in the TractTrace site, brand, and related materials. A paid
          order, if you later accept one, grants only the license described in that order.
        </p>
      </Section>

      <Section title="8. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Junoware&apos;s aggregate liability arising out of
          the Service will not exceed the fees you paid to Junoware for the Service in the twelve
          (12) months before the claim. We are not liable for indirect, incidental, special, or
          consequential damages, including lost profits, lost deals, or missed acquisition
          opportunities.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          We may update these Terms by posting a revised version on this page. Material changes
          will be dated above. Continued use after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          Junoware, LLC · TractTrace product ·{' '}
          <a href="mailto:contact@junoware.com" className="text-cyan-300 hover:text-cyan-200">
            contact@junoware.com
          </a>
        </p>
      </Section>
    </LegalDocLayout>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalDocLayout eyebrow="Legal" title="Privacy Policy" updated="October 28, 2026">
      <Section title="1. Scope">
        <p>
          This Privacy Policy describes how Junoware, LLC collects, uses, and shares information
          when you use TractTrace websites, qualification forms, and related services. TractTrace
          is a B2B commercial permit coverage product. This Policy applies to our public site and
          sales intake, not to any exclusive data partnership unless that partnership is documented
          separately.
        </p>
      </Section>

      <Section title="2. Public B2B data notice">
        <p>
          The site may show{' '}
          <strong className="font-semibold text-slate-100">public municipal permit filings</strong>{' '}
          as examples, and a specialist may discuss public commercial building permits with you on
          a call. The current public offering is that sales conversation, not an automated GC
          outreach feed.
        </p>
      </Section>

      <Section title="3. Information we collect from visitors">
        <ul className="list-disc space-y-2 pl-5 text-slate-300">
          <li>
            <span className="text-slate-100">Commercial intake:</span> name, work email, phone
            number, company, territory or metro of interest, and role. We also receive IP address
            and user agent with the submission. Submitting the form is a request for a call.
          </li>
          <li>
            <span className="text-slate-100">Form funnel signals:</span> whether a visitor started,
            submitted, or left the audit form, plus path, referrer, a session identifier, and the
            metro/role they entered if they submit.
          </li>
          <li>
            <span className="text-slate-100">Product feedback:</span> ratings, comments, optional
            email, and technical metadata (URL, timezone, screen size, referrer, IP, user agent).
          </li>
          <li>
            <span className="text-slate-100">Site visit ops signals:</span> landing path, referrer /
            UTM parameters, approximate IP-based location, device and browser metadata used for
            internal traffic monitoring (not sold or used as a consumer lead product).
          </li>
          <li>
            <span className="text-slate-100">Internal ops console:</span> if an authorized operator
            signs in, we collect that person&apos;s email, display name, and authentication
            identifiers. Public visitors are not asked to create an account, and this site does not
            process card payments.
          </li>
        </ul>
      </Section>

      <Section title="4. How we use information">
        <p>
          We use this information to qualify sales-desk interest, call you when you request metro
          coverage, send the follow-ups you asked for, watch for abuse, improve the site, and comply
          with law. Intake submissions are sent to Junoware by email and to an internal ops channel.
          We do not sell visitor information as a consumer lead list.
        </p>
      </Section>

      <Section title="5. Stop-contact requests">
        <p>
          If you submitted the form and do not want a call, or you want us to stop emailing you,
          write{' '}
          <a href="mailto:contact@junoware.com" className="text-cyan-300 hover:text-cyan-200">
            contact@junoware.com
          </a>
          . We will stop Junoware outreach to that number or address where reasonably practicable.
          Public permit records on municipal sites are not something we can delete.
        </p>
      </Section>

      <Section title="6. Third-party data handlers">
        <p>We use these processors for the public site and intake:</p>
        <ul className="list-disc space-y-2 pl-5 text-slate-300">
          <li>
            <span className="text-slate-100">Netlify</span> - website hosting and the intake
            functions that receive form, visit, funnel, and feedback posts.
          </li>
          <li>
            <span className="text-slate-100">Mailtrap and/or SMTP</span> - transactional email that
            forwards intake (and, when email is configured, feedback) to Junoware.
          </li>
          <li>
            <span className="text-slate-100">Discord</span> - internal ops alerts for site visits,
            form funnel events, feedback, and intake submissions (including name, work email, phone,
            company, metro, role, IP, and user agent when you submit).
          </li>
          <li>
            <span className="text-slate-100">Google Firebase</span> - loaded in the browser for
            internal ops authentication. Public visitors are not required to sign in.
          </li>
        </ul>
        <p>
          We may also share information with professional advisors or successors in a corporate
          transaction, and when required by law or to protect rights and safety.
        </p>
      </Section>

      <Section title="7. Retention &amp; security">
        <p>
          We keep intake, feedback, and ops-alert records as long as needed to follow up, operate
          the site, and meet legal obligations. No method of transmission or storage is perfectly
          secure.
        </p>
      </Section>

      <Section title="8. Your choices">
        <p>
          You may request access, correction, or deletion of personal information we hold about you
          as a site visitor by emailing{' '}
          <a href="mailto:contact@junoware.com" className="text-cyan-300 hover:text-cyan-200">
            contact@junoware.com
          </a>
          , subject to legitimate retention needs (security logs, fraud prevention). Stop-contact
          requests are handled under Section 5.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          The Service is directed to businesses and professional buyers. It is not intended for
          individuals under 16.
        </p>
      </Section>

      <Section title="10. Changes">
        <p>
          We may update this Policy by posting a revised version with a new &quot;Last
          updated&quot; date. Material changes will appear on this page.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          Junoware, LLC · Privacy inquiries ·{' '}
          <a href="mailto:contact@junoware.com" className="text-cyan-300 hover:text-cyan-200">
            contact@junoware.com
          </a>
        </p>
      </Section>
    </LegalDocLayout>
  );
}
